// ==================== CONTACTS SERVICE ====================

import api from './api';
import type { 
  Contact, 
  ContactGroup, 
  ContactCustomField, 
  ContactStats, 
  ImportResult,
  PaginatedResponse,
  AutoAddRules
} from '../types';

// Re-export types for convenience
export type { Contact, ContactGroup, ContactCustomField, ContactStats };

// ==================== QUERY TYPES ====================

export interface QueryContactsParams {
  search?: string;
  tags?: string[];
  groupIds?: string[];
  optedIn?: boolean;
  sortBy?: 'name' | 'phoneNumber' | 'email' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// ==================== GROUP MEMBERS TYPES ====================

export interface GroupMember {
  id: string;
  addedAt: string;
  contact: {
    id: string;
    name?: string;
    phoneNumber: string;
    email?: string;
    country?: string;
    optedIn: boolean;
    createdAt: string;
    tags: string[];
  };
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GroupSummary {
  id: string;
  name: string;
  color?: string;
  type: 'STATIC' | 'DYNAMIC';
}

export interface GroupMembersResponse {
  items: GroupMember[];
  meta: PaginationMeta;
  group: GroupSummary;
}

export interface AvailableContact {
  id: string;
  name?: string;
  phoneNumber: string;
  email?: string;
  country?: string;
  optedIn: boolean;
  createdAt: string;
  tags: string[];
}

export interface AvailableContactsResponse {
  items: AvailableContact[];
  meta: PaginationMeta;
  group: { id: string; name: string };
}

export interface CreateContactData {
  phoneNumber: string;
  name?: string;
  email?: string;
  company?: string;
  country?: string;
  notes?: string;
  tags?: string[];
  customFields?: Record<string, unknown>;
  optedIn?: boolean;
  groupIds?: string[];
  /** Multiple organizations: the selected account for its groups and custom fields */
  organizationId?: string;
}

export interface UpdateContactData {
  phoneNumber?: string;
  name?: string;
  email?: string;
  company?: string;
  country?: string;
  notes?: string;
  tags?: string[];
  customFields?: Record<string, unknown>;
  optedIn?: boolean;
  groupIds?: string[];
  /** Multiple organizations: the selected account for its groups and custom fields */
  organizationId?: string;
}

export interface ImportContactsData {
  contacts: Array<{
    phoneNumber: string;
    name?: string;
    email?: string;
    company?: string;
    country?: string;
    notes?: string;
    tags?: string[];
  }>;
  skipDuplicates?: boolean;
  updateExisting?: boolean;
  groupIds?: string[];
  /** Multiple organizations: the selected account for import groups */
  organizationId?: string;
}

export interface CreateGroupData {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  type?: 'STATIC' | 'DYNAMIC';
  autoAddRules?: AutoAddRules;
  isDefault?: boolean;
  /** Multiple organizations: the organization it is created in (required when the customer has more than one) */
  organizationId?: string;
}

export interface UpdateGroupData {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  type?: 'STATIC' | 'DYNAMIC';
  autoAddRules?: AutoAddRules;
  isDefault?: boolean;
}

export interface CreateCustomFieldData {
  name: string;
  label: string;
  type?: 'TEXT' | 'NUMBER' | 'DATE' | 'EMAIL' | 'PHONE' | 'URL' | 'SELECT' | 'MULTI_SELECT' | 'BOOLEAN';
  isRequired?: boolean;
  options?: string[];
  sortOrder?: number;
  /** Multiple organizations: the organization it is created in (required when the customer has more than one) */
  organizationId?: string;
}

export interface UpdateCustomFieldData {
  label?: string;
  isRequired?: boolean;
  options?: string[];
  sortOrder?: number;
}

// Extended API response with meta for paginated endpoints
interface PaginatedApiResponse<T> {
  success: boolean;
  message: string;
  data?: T[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ==================== CONTACTS API ====================

const organizationQuery = (organizationId?: string) =>
  organizationId ? `?organizationId=${organizationId}` : '';

export const contactsService = {
  // ==================== CONTACT CRUD ====================

  async getAll(params?: QueryContactsParams): Promise<PaginatedResponse<Contact>> {
    const queryParams = new URLSearchParams();
    
    if (params?.search) queryParams.append('search', params.search);
    if (params?.tags?.length) queryParams.append('tags', params.tags.join(','));
    if (params?.groupIds?.length) queryParams.append('groupIds', params.groupIds.join(','));
    if (params?.optedIn !== undefined) queryParams.append('optedIn', String(params.optedIn));
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const queryString = queryParams.toString();
    const url = queryString ? `/customer/contacts?${queryString}` : '/customer/contacts';
    
    const response = await api.get(url) as PaginatedApiResponse<Contact>;
    
    // response.data is the contacts array, response.meta is the pagination meta
    const data = response.data;
    const meta = response.meta;
    
    return {
      items: Array.isArray(data) ? data : [],
      meta: meta || {
        total: Array.isArray(data) ? data.length : 0,
        page: params?.page || 1,
        limit: params?.limit || 20,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  },

  async getById(id: string): Promise<Contact> {
    const response = await api.get(`/customer/contacts/${id}`);
    return response.data as Contact;
  },

  async getByIds(ids: string[]): Promise<Contact[]> {
    const response = await api.post('/customer/contacts/by-ids', { ids });
    return (response.data as Contact[]) || [];
  },

  async create(data: CreateContactData): Promise<Contact> {
    const response = await api.post('/customer/contacts', data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create contact');
    }
    return response.data as Contact;
  },

  async update(id: string, data: UpdateContactData): Promise<Contact> {
    const response = await api.put(`/customer/contacts/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update contact');
    }
    return response.data as Contact;
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/customer/contacts/${id}`);
    if (!response.success) {
      throw response; // Throw the error response so the caller can catch it
    }
  },

  async bulkDelete(contactIds: string[]): Promise<{ deleted: number; skipped: number; errors: string[] }> {
    const response = await api.post('/customer/contacts/bulk-delete', { contactIds });
    return (response.data as { deleted: number; skipped: number; errors: string[] }) || { deleted: 0, skipped: 0, errors: [] };
  },

  // ==================== OPT-IN/OUT ====================

  async optOut(id: string): Promise<Contact> {
    const response = await api.post(`/customer/contacts/${id}/opt-out`);
    return response.data as Contact;
  },

  async optIn(id: string): Promise<Contact> {
    const response = await api.post(`/customer/contacts/${id}/opt-in`);
    return response.data as Contact;
  },

  // ==================== IMPORT/EXPORT ====================

  async import(data: ImportContactsData): Promise<ImportResult> {
    const response = await api.post('/customer/contacts/import', data);
    return response.data as ImportResult;
  },

  async export(params?: QueryContactsParams): Promise<Contact[]> {
    const response = await api.post('/customer/contacts/export', params || {});
    return (response.data as Contact[]) || [];
  },

  // ==================== STATS & TAGS ====================

  async getStats(organizationId?: string): Promise<ContactStats> {
    const response = await api.get(`/customer/contacts/stats${organizationQuery(organizationId)}`);
    return (response.data as ContactStats) || { total: 0, optedIn: 0, optedOut: 0, byGroup: [] };
  },

  async getAllTags(): Promise<string[]> {
    const response = await api.get('/customer/contacts/tags');
    return (response.data as string[]) || [];
  },

  // ==================== GROUPS ====================

  async getGroups(organizationId?: string): Promise<ContactGroup[]> {
    const response = await api.get(`/customer/contacts/groups/list${organizationQuery(organizationId)}`);
    return (response.data as ContactGroup[]) || [];
  },

  async getActiveGroups(organizationId?: string): Promise<ContactGroup[]> {
    const response = await api.get(`/customer/contacts/groups/active${organizationQuery(organizationId)}`);
    return (response.data as ContactGroup[]) || [];
  },

  async getGroup(id: string): Promise<ContactGroup> {
    const response = await api.get(`/customer/contacts/groups/${id}`);
    return response.data as ContactGroup;
  },

  async createGroup(data: CreateGroupData): Promise<ContactGroup> {
    const response = await api.post('/customer/contacts/groups', data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create group');
    }
    return response.data as ContactGroup;
  },

  async updateGroup(id: string, data: UpdateGroupData): Promise<ContactGroup> {
    const response = await api.put(`/customer/contacts/groups/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update group');
    }
    return response.data as ContactGroup;
  },

  async deleteGroup(id: string): Promise<void> {
    const response = await api.delete(`/customer/contacts/groups/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete group');
    }
  },

  async addContactsToGroup(groupId: string, contactIds: string[]): Promise<{ added: number }> {
    const response = await api.post(`/customer/contacts/groups/${groupId}/add`, { contactIds });
    return (response.data as { added: number }) || { added: 0 };
  },

  async removeContactsFromGroup(groupId: string, contactIds: string[]): Promise<{ removed: number }> {
    const response = await api.post(`/customer/contacts/groups/${groupId}/remove`, { contactIds });
    return (response.data as { removed: number }) || { removed: 0 };
  },

  async getGroupMembers(
    groupId: string, 
    params?: { search?: string; optedIn?: boolean; page?: number; limit?: number }
  ): Promise<GroupMembersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.optedIn !== undefined) queryParams.append('optedIn', String(params.optedIn));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    
    const url = `/customer/contacts/groups/${groupId}/members${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    
    const responseData = response as unknown as { data: GroupMember[]; meta: PaginationMeta; group: GroupSummary };
    return {
      items: responseData.data || [],
      meta: responseData.meta,
      group: responseData.group,
    };
  },

  async getAvailableContactsForGroup(
    groupId: string,
    params?: { search?: string; page?: number; limit?: number }
  ): Promise<AvailableContactsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    
    const url = `/customer/contacts/groups/${groupId}/available-contacts${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await api.get(url);
    
    const responseData = response as unknown as { data: AvailableContact[]; meta: PaginationMeta; group: { id: string; name: string } };
    return {
      items: responseData.data || [],
      meta: responseData.meta,
      group: responseData.group,
    };
  },

  // ==================== CUSTOM FIELDS ====================

  async getCustomFields(organizationId?: string): Promise<ContactCustomField[]> {
    try {
      const response = await api.get(`/customer/contacts/custom-fields/list${organizationQuery(organizationId)}`);
      return (response.data as ContactCustomField[]) || [];
    } catch {
      console.error('Failed to fetch custom fields');
      return [];
    }
  },

  async createCustomField(data: CreateCustomFieldData): Promise<ContactCustomField> {
    const response = await api.post('/customer/contacts/custom-fields', data);
    return response.data as ContactCustomField;
  },

  async updateCustomField(id: string, data: UpdateCustomFieldData): Promise<ContactCustomField> {
    const response = await api.put(`/customer/contacts/custom-fields/${id}`, data);
    return response.data as ContactCustomField;
  },

  async deleteCustomField(id: string): Promise<void> {
    await api.delete(`/customer/contacts/custom-fields/${id}`);
  },
};

export default contactsService;
